import { createSlice } from '@reduxjs/toolkit';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { ServiceOrderStatus, ServiceOrderType } from '../../types/service-order/service-order.type';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import { getServiceOrdersList, postSiteSurveyOutcome } from './tcServiceOrders.action';
import snackbarUtils from '../../utils/SnackbarUtils';

export interface TcServiceOrdersState {
  serviceOrdersList: null | PaginationType<ServiceOrderType>;
  serviceOrdersListFetchStatus: FETCH_STATUS;
  serviceOrdersRouteParams: RouteParamsType & { status: ServiceOrderStatus | '' };
  siteSurveyOutcomePostStatus: FETCH_STATUS;
}

export const initialServiceOrdersRouteParams = {
  page: 1,
  limit: 10,
  order: '-id',
  q: '',
  status: '' as const,
};

const INIT_STATE: TcServiceOrdersState = {
  serviceOrdersList: null,
  serviceOrdersListFetchStatus: FETCH_STATUS.NULL,
  serviceOrdersRouteParams: initialServiceOrdersRouteParams,
  siteSurveyOutcomePostStatus: FETCH_STATUS.NULL,
};

export const tcServiceOrdersSlice = createSlice({
  name: 'tcServiceOrders',
  initialState: INIT_STATE,
  reducers: {
    setServiceOrdersList: (state, action) => {
      state.serviceOrdersList = action.payload;
    },
    setServiceOrdersListFetchStatus: (state, action) => {
      state.serviceOrdersListFetchStatus = action.payload;
    },
    setServiceOrdersRouteParams: (state, action) => {
      state.serviceOrdersRouteParams = { ...state.serviceOrdersRouteParams, ...action.payload };
    },
  },
  extraReducers: builder => {
    builder.addCase(SET_ROUTE_PARAMS, (state, action) => {
      const { payload } = action as ToolkitActionType<{ reducer: string; data: RouteParamsType }>;
      if (payload.reducer === 'serviceOrders') {
        state.serviceOrdersRouteParams = { ...state.serviceOrdersRouteParams, ...payload.data };
      }
    });

    builder.addCase(getServiceOrdersList.pending, state => {
      state.serviceOrdersListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getServiceOrdersList.rejected, state => {
      state.serviceOrdersListFetchStatus = FETCH_STATUS.REJECTED;
    });
    builder.addCase(getServiceOrdersList.fulfilled, (state, action) => {
      state.serviceOrdersListFetchStatus = FETCH_STATUS.FULFILLED;
      state.serviceOrdersList = action.payload;
    });
    builder.addCase(postSiteSurveyOutcome.pending, state => {
      state.siteSurveyOutcomePostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postSiteSurveyOutcome.fulfilled, (state, action) => {
      state.siteSurveyOutcomePostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success(action.payload.message));
    });
    builder.addCase(postSiteSurveyOutcome.rejected, (state, action) => {
      state.siteSurveyOutcomePostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => snackbarUtils.error(action.payload as string));
    });
  },
});

export const { setServiceOrdersList, setServiceOrdersListFetchStatus, setServiceOrdersRouteParams } =
  tcServiceOrdersSlice.actions;

export default tcServiceOrdersSlice.reducer;
